import { AxiosResponse } from "axios";
import { CloudUpload, File, Loader2 } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

import { toast } from "sonner";
import { catchAsync, checkAnyFileToBeUpload, cn, successTheme } from "src/helpers";
import { getUserProfileRequestApi, updateUserProfileRequestApi } from "src/services/requests/Auth";
import LoadingDialog from "src/shadcn/components/custom/LoadingDialog";
import { Button, Input } from "src/shadcn/components/ui";
type Props = {
  profilData: any;
  loading: boolean;
};
const NDADocuments = ({ profilData, loading }: Props) => {
  const [fileName, setFileName] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [data, setData] = useState<any>({});
  const [isLoading, setisLoading] = useState<boolean>(false);
  const [response, setResponse] = useState<any>({});
  const { nda_signed_doc, ...rest } = data;
  useEffect(() => {
    setData(profilData);
  }, [profilData, loading]);
  const handleFocus = () => {
    if (inputRef.current) {
      inputRef.current.click(); // Focuses the input field
    }
  };

  const handleUpdateProfileDetails = catchAsync(
    async (values: any) => {
      setisLoading(true);
      return await updateUserProfileRequestApi(values);
    },
    (result: AxiosResponse) => {
      if (result.status === 200) {
        toast.success("Documents updated successfully", {
          style: successTheme,
        });
        setResponse(result?.data);
      }
      setisLoading(false);
    },
    () => {
      setisLoading(false);
    }
  );

  const handleToCheckAnyDocumentToBeUpload = catchAsync(
    async (values: any) => {
      setisLoading(true);
      return await checkAnyFileToBeUpload(values);
    },
    async (result: any) => {
      if (result[1]) {
        const postObj = {
          ...rest,
          ...result[0],
        };

        setisLoading(false);
        await handleUpdateProfileDetails(postObj);
        await handleFetchProfileDetails();
        setFileName("");
      }
    },
    () => {
      setisLoading(false);
    }
  );

  const handleFetchProfileDetails = catchAsync(
    async () => {
      setisLoading(true);
      return await getUserProfileRequestApi();
    },
    (result: any) => {
      if (result.success) {
        setData(result.data);
      }
      setisLoading(false);
    },
    () => {
      setisLoading(false);
    }
  );
  return (
    <div className="w-full h-full space-y-8">
      <div className="flex items-center justify-end space-x-2">
        <span className="text-gray-700 font-medium">NDA Status :</span>
        <span className="px-3 py-1 rounded-full text-sm font-semibold" id="status-badge">
          {data?.nda_status ? data?.nda_status : "-"}
        </span>
      </div>

      <div className="w-full grid grid-cols-2 gap-6">
        <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 flex flex-col items-center space-y-3 text-center">
          <File />

          <p className="text-sm text-gray-800">Unsigned Documents</p>
          <p className="text-xs text-gray-400">
            To proceed, download the unsigned document, add your signature, and re-upload it
          </p>
          {data?.nda_un_signed_doc ? (
            <div className="space-y-2">
              <div className="text-xs text-blue-400">
                {data?.nda_un_signed_doc.split("/")[data?.nda_un_signed_doc.split("/")?.length - 1]}
              </div>
              <div className="space-x-2">
                <Button
                  variant={"outline"}
                  className="w-[100px] border-gray-400 text-gray-600 bg-white hover:bg-white hover:text-gray-800"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open(data?.nda_un_signed_doc, "_blank");
                  }}
                >
                  View
                </Button>

                <a href={`/${data?.nda_un_signed_doc}`} download="Unsigned">
                  <Button
                    variant={"outline"}
                    className="w-[100px] border-gray-400 text-gray-600 bg-white hover:bg-white hover:text-gray-800"
                    // onClick={handleDownload}
                  >
                    Download
                  </Button>
                </a>
              </div>
            </div>
          ) : (
            <div className="text-xs font-normal text-gray-400">No documents Available</div>
          )}
        </div>

        <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 flex flex-col items-center space-y-3 text-center">
          <CloudUpload />

          <p className="text-sm text-gray-800">Signed Documents</p>
          <p className="text-xs text-gray-400">Uplood signed document here.</p>
          <p className={cn("text-sm flex", { hidden: !fileName })}>{fileName ? fileName : ""}</p>
          {isLoading && (
            <div className="flex items-center space-x-1">
              <Loader2 className=" h-4 w-4 animate-spin" /> <span className="text-xs font-medium">Please Wait...</span>{" "}
            </div>
          )}
          <Input
            type="file"
            ref={inputRef}
            className="hidden"
            onChange={(e: any) => {
              setFileName(e.target.files[0]?.name);

              const bolbData = {
                nda_signed_doc: e.target.files && e.target.files[0],
              };
              handleToCheckAnyDocumentToBeUpload(bolbData);
            }}
            accept=".pdf"
          />
          {data?.nda_signed_doc ? (
            <div className="space-y-2">
              <div className="text-xs text-blue-400">
                {data?.nda_signed_doc.split("/")[data?.nda_signed_doc.split("/")?.length - 1]}
              </div>
              <div className="space-x-2 flex items-center justify-center">
                <Button
                  variant={"outline"}
                  className="w-[100px] border-gray-400 text-gray-600 bg-white hover:bg-white hover:text-gray-800"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open(data?.nda_signed_doc, "_blank");
                  }}
                >
                  View
                </Button>
                <Button
                  variant={"outline"}
                  className={cn(
                    "w-[100px] border-gray-400 text-gray-600 bg-white hover:bg-white hover:text-gray-800 flex",
                    { hidden: isLoading }
                  )}
                  onClick={handleFocus}
                >
                  Update
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant={"outline"}
              className={cn(
                "w-[100px] border-gray-400 text-gray-600 bg-white hover:bg-white hover:text-gray-800 flex",
                { hidden: isLoading }
              )}
              onClick={handleFocus}
            >
              Upload
            </Button>
          )}
        </div>
      </div>
      <LoadingDialog isOpen={loading} />
    </div>
  );
};

export default NDADocuments;
